import {computed, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {catchError, of, tap} from 'rxjs';
import {environment} from '../../../environment';
import {Account} from './account.model';
import {SpinnerOverlayService} from '../Components/spinner/spinner-overlay.service';

@Injectable({providedIn: 'root'})
export class AccountService {
  private apiAccount = `${environment.apiBaseUrl}/auth/account`

  private readonly authenticationKey = 'token';
  private isLoggingOut = false;

  readonly userIdentity = signal<Account | null>(null);
  readonly selectedWarehouseCode = signal<string | null>(null);

  readonly isAuthenticated = computed(() => this.userIdentity() !== null);
  readonly authorities = computed(() => this.userIdentity()?.user.roles ?? []);
  readonly warehouseCodes = computed(() => this.userIdentity()?.user.warehouseCodes ?? []);

  constructor(
    private http: HttpClient,
    private router: Router,
    private spinner: SpinnerOverlayService
  ) {
  }

  trackCurrentAccount() {
    return this.userIdentity.asReadonly();
  }

  loadIdentity(force = false) {
    if (this.userIdentity() && !force) {
      return of(this.userIdentity());
    }

    const token = localStorage.getItem(this.authenticationKey);
    if (!token) {
      this.userIdentity.set(null);
      this.selectedWarehouseCode.set(null);
      return of(null);
    }

    this.spinner.show();
    return this.http
      .get<Account>(this.apiAccount)
      .pipe(
        tap(account => {
          this.userIdentity.set(account);
          this.validateSelectedWarehouse(account);
          this.spinner.hide()
        }),
        catchError(() => {
          this.userIdentity.set(null);
          this.selectedWarehouseCode.set(null);
          this.spinner.hide()
          return of(null);
        })
      );
  }

  private validateSelectedWarehouse(account: Account) {
    const warehouseCode = account.user.warehouseCode;
    const warehouseCodes = account.user.warehouseCodes ?? [];
    
    if (warehouseCode && warehouseCodes.includes(warehouseCode)) {
      this.selectedWarehouseCode.set(warehouseCode);
    } else if (warehouseCodes.length === 1) {
      this.selectedWarehouseCode.set(warehouseCodes[0]);
      this.persistWarehouseToBackend(warehouseCodes[0]);
    } else {
      this.selectedWarehouseCode.set(null);
    }
  }

  private persistWarehouseToBackend(code: string) {
    const userId = this.userIdentity()?.user.id;
    if (!userId) return;
    this.http.put(`${environment.wsApiBaseUrl}/api/user/${userId}/warehouse`, { warehouseCode: code })
      .subscribe({
        error: (err) => console.error('Error updating warehouse', err)
      });
  }

  selectWarehouse(code: string) {
    this.selectedWarehouseCode.set(code);
  }

  updateSelectedWarehouse(code: string) {
    const userId = this.userIdentity()?.user.id;
    if (!userId) return;

    this.http.put(`${environment.wsApiBaseUrl}/api/user/${userId}/warehouse`, { warehouseCode: code })
      .subscribe({
        next: () => {
          this.selectWarehouse(code);
        },
        error: (err) => {
          console.error('Error updating warehouse', err);
        }
      });
  }

  hasAnyAuthority(authorities: string[] | string): boolean {
    const user = this.userIdentity();
    if (!user) return false;
    const userRoles = user.user.roles.map(r => r.code ?? '');
    const roles = Array.isArray(authorities) ? authorities : [authorities];
    return roles.some(r => userRoles.includes(r));
  }

  logout() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    this.userIdentity.set(null);
    this.selectedWarehouseCode.set(null);

    const keysToClear = [
      this.authenticationKey,
      'orderId',
      'orderNumber',
      'my_cart_items',
      'orderItems',
      'cart'
    ];

    keysToClear.forEach(key => localStorage.removeItem(key));

    const currentUrl = this.router.url ?? '';
    const navigationPromise = currentUrl.startsWith('/login')
      ? Promise.resolve(false)
      : this.router.navigateByUrl('/login', { replaceUrl: true });

    navigationPromise.finally(() => {
      this.isLoggingOut = false;
    });
  }

  markAuthenticated() {
    this.isLoggingOut = false;
  }

}
